/**
 * Merge Candidates — review surface for likely duplicates surfaced by
 * Cynapse's on-demand consolidation (and the historical trigram band).
 *
 * Two tabs share the same skeleton: Contacts (5,500+ entity tier),
 * Companies (2,800+ entity tier). Each row shows the incoming duplicate
 * vs the canonical, similarity score, the match method that flagged it,
 * and per-row Approve / Reject buttons. Approve = execute the merge
 * (re-key announcement_contacts / company_announcements, union the
 * canonical's data fields, delete the duplicate global_entity). Reject =
 * record the operator decision so future ingests don't re-queue.
 *
 * Approve is destructive (deletes a global_entity row), so it's gated
 * behind a confirm prompt. Reject is reversible-by-policy (the partial-
 * unique index ensures the same pair won't re-queue unless rejection
 * is undone manually).
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, XCircle, AlertCircle, RefreshCw, Users, Building2,
  Loader2, Briefcase, History, UserCheck, GitMerge,
} from 'lucide-react';
import {
  listContactMergeCandidates,
  listCompanyMergeCandidates,
  listFundMergeCandidates,
  listAttributeChangeCandidates,
  approveContactMerge,
  rejectContactMerge,
  approveCompanyMerge,
  rejectCompanyMerge,
  revertCompanyMerge,
  approveFundMerge,
  rejectFundMerge,
  approveAttributeChange,
  rejectAttributeChange,
  listIdentityGroups,
  mergeIdentityGroup,
  separateIdentity,
  unlinkIdentityContact,
  type IdentityGroup,
  type ContactMergeCandidateRow,
  type CompanyMergeCandidateRow,
  type FundMergeCandidateRow,
  type AttributeChangeCandidateRow,
  type MergeReviewStatus,
  type AttributeChangeReviewStatus,
} from '../lib/api/merge-candidates.js';

type Tab = 'identities' | 'contacts' | 'companies' | 'attribute-changes' | 'funds';

type StatusFilter = MergeReviewStatus | 'all';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'pending',           label: 'Pending' },
  { value: 'approved_merge',    label: 'Merged' },
  { value: 'rejected_separate', label: 'Rejected' },
  { value: 'superseded',        label: 'Auto-resolved' },
  { value: 'approved_link',     label: 'Linked' },
  { value: 'all',               label: 'All' },
];

const STATUS_BADGE: Record<MergeReviewStatus, { label: string; cls: string }> = {
  pending:           { label: 'Pending',       cls: 'bg-grey-100 text-grey-700 border border-grey-200' },
  approved_merge:    { label: 'Merged',        cls: 'bg-success-50 text-success-700 border border-success-200' },
  rejected_separate: { label: 'Rejected',      cls: 'bg-danger-50 text-danger-700 border border-danger-200' },
  expired:           { label: 'Expired',       cls: 'bg-grey-50 text-grey-500 border border-grey-200' },
  superseded:        { label: 'Auto-resolved', cls: 'bg-info-50 text-info-700 border border-info-200' },
  approved_link:     { label: 'Linked',        cls: 'bg-success-50 text-success-700 border border-success-200' },
};

export default function MergeCandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // `kind`, not `tab`: inside the review queue (?tab=merge) the page's own tab
  // in `tab` made the queue fall back to its first tab (2026-09-22). Old deep
  // links (/entities/merge-review?tab=companies) still read.
  const tabParam = searchParams.get('kind') ?? searchParams.get('tab');
  const initialTab: Tab =
    tabParam === 'contacts'          ? 'contacts'
    : tabParam === 'companies'       ? 'companies'
    : tabParam === 'attribute-changes' ? 'attribute-changes'
    : tabParam === 'funds'           ? 'funds'
    : 'identities';
  const [tab, _setTab] = useState<Tab>(initialTab);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const qc = useQueryClient();

  const setTab = (next: Tab) => {
    _setTab(next);
    // Keep the URL in sync so the back/forward button + deep links from /tools
    // and the Dashboard stay on the same tab without bouncing back to Contacts.
    const sp = new URLSearchParams(searchParams);
    sp.set('kind', next);
    setSearchParams(sp, { replace: true });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <Link to="/entities" className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-grey-800 mb-2">
          <ArrowLeft className="h-4 w-4" /> Back to entities
        </Link>
        <h1 className="text-2xl font-semibold text-grey-900">Merge Review</h1>
        <p className="text-sm text-grey-600 mt-1">
          Likely-duplicate entities surfaced by Cynapse's on-demand consolidation. Approve to merge the duplicate into the canonical (re-keys announcement links, unions data, deletes the orphan). Reject to confirm they're separate entities — future ingests won't re-queue the pair.
        </p>
      </header>

      {/* Tabs: Contacts | Companies */}
      <div className="flex gap-2 border-b border-grey-200 mb-4">
        <button
          onClick={() => setTab('identities')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === 'identities' ? 'border-primary-600 text-primary-700' : 'border-transparent text-grey-600 hover:text-grey-900'
          }`}
        >
          <UserCheck className="h-4 w-4" /> Identities
        </button>
        <button
          onClick={() => setTab('contacts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === 'contacts' ? 'border-primary-600 text-primary-700' : 'border-transparent text-grey-600 hover:text-grey-900'
          }`}
        >
          <Users className="h-4 w-4" /> Contacts
        </button>
        <button
          onClick={() => setTab('companies')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === 'companies' ? 'border-primary-600 text-primary-700' : 'border-transparent text-grey-600 hover:text-grey-900'
          }`}
        >
          <Building2 className="h-4 w-4" /> Companies
        </button>
        <button
          onClick={() => setTab('attribute-changes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === 'attribute-changes' ? 'border-primary-600 text-primary-700' : 'border-transparent text-grey-600 hover:text-grey-900'
          }`}
        >
          <History className="h-4 w-4" /> Attribute Changes
        </button>
        <button
          onClick={() => setTab('funds')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${
            tab === 'funds' ? 'border-primary-600 text-primary-700' : 'border-transparent text-grey-600 hover:text-grey-900'
          }`}
        >
          <Briefcase className="h-4 w-4" /> Funds
        </button>
      </div>

      {/* Status filter (not for identity groups: they are open until decided) */}
      <div className={`flex gap-2 mb-4 ${tab === 'identities' ? 'hidden' : ''}`}>
        {STATUS_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setStatusFilter(opt.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              statusFilter === opt.value
                ? 'bg-primary-600 text-white'
                : 'bg-surface text-grey-700 border border-grey-300 hover:bg-grey-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {tab === 'identities' && <IdentitiesList qc={qc} />}
      {tab === 'contacts' && <ContactsList statusFilter={statusFilter} qc={qc} />}
      {tab === 'companies' && <CompaniesList statusFilter={statusFilter} qc={qc} />}
      {tab === 'attribute-changes' && <AttributeChangesList statusFilter={statusFilter} qc={qc} />}
      {tab === 'funds' && <FundsList statusFilter={statusFilter} qc={qc} />}
    </div>
  );
}

// =============================================================================
// Contacts list
// =============================================================================
function ContactsList({ statusFilter, qc }: { statusFilter: StatusFilter; qc: ReturnType<typeof useQueryClient> }) {
  const pg = usePagedQueue(`contacts:${statusFilter}`);
  const queryKey = ['merge-candidates', 'contacts', statusFilter, pg.page, pg.q];
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: () => listContactMergeCandidates(statusFilter, pg.params),
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  // Person rows (2026-09-20): pass the pair on screen so assayer re-points
  // key_role_bindings before Cynapse deletes the loser.
  const approveWithIds = (id: string) => {
    const row = rows.find(r => r.candidate_id === id);
    // A cross_type approve links (nothing is deleted), so no pair is passed and
    // assayer re-points no bindings.
    if (row?.match_method === 'cross_type') return approveContactMerge(id);
    const dup = (row?.incoming_payload as Record<string, unknown> | undefined)?.duplicate_entity_id;
    return approveContactMerge(id, undefined, row
      ? { canonical_entity_id: row.matched_contact_id, duplicate_entity_id: typeof dup === 'string' ? dup : null }
      : undefined);
  };
  const bulk = useBulkMergeOps(
    approveWithIds,
    rejectContactMerge,
    qc,
    `contacts:${statusFilter}:${pg.page}:${pg.q}`,
  );

  if (isLoading) return <div className="text-sm text-grey-500">Loading candidates…</div>;
  if (error) return <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">Failed to load: {(error as Error).message}</div>;

  const rows = data?.items ?? [];
  const pendingIds = rows.filter(r => r.review_status === 'pending').map(r => r.candidate_id);

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <SelectAllCheckbox
            pendingIds={pendingIds}
            selected={bulk.selected}
            onSelectAll={bulk.selectAll}
            onClear={bulk.clearSelection}
          />
          <QueueCount total={data?.total ?? 0} offset={data?.offset ?? 0} shown={rows.length} />
        </div>
        <button onClick={() => refetch()} disabled={isRefetching} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-600 hover:text-grey-900 border border-grey-200 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <QueuePager pg={pg} total={data?.total ?? 0} search={true} />

      <BulkActionBar
        selectedCount={bulk.selected.size}
        approveLabel={`Approve ${bulk.selected.size} merge${bulk.selected.size === 1 ? '' : 's'}`}
        approveConfirm={
          `Approve ${bulk.selected.size} contact merge${bulk.selected.size === 1 ? '' : 's'}?\n\n` +
          `For each pair this re-keys announcement_contacts to the canonical, unions contact_data and email_addresses, and DELETES the duplicate global_entity. Cannot be undone via the UI.`
        }
        rejectLabel={`Mark ${bulk.selected.size} as not duplicates`}
        rejectConfirm={
          `Mark ${bulk.selected.size} pair${bulk.selected.size === 1 ? '' : 's'} as not duplicates?\n\n` +
          `Future ingests won't re-queue these pairs unless the rejection is undone manually.`
        }
        progress={bulk.progress}
        onApprove={(ct) => bulk.runBulk('approve', ct)}
        onReject={(ct) => bulk.runBulk('reject', ct)}
        onCancel={bulk.cancelBulk}
        onClear={bulk.clearSelection}
        onDismissSummary={bulk.dismissSummary}
      />

      {rows.length === 0 ? (
        <EmptyState message="No contact-merge candidates in this view." />
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <ContactRow
              key={row.candidate_id}
              row={row}
              selected={bulk.selected.has(row.candidate_id)}
              onToggleSelect={() => bulk.toggleRow(row.candidate_id)}
              onApprove={() => bulk.runIds('approve', [row.candidate_id],
                row.match_method === 'cross_type'
                  ? `Link contact "${row.incoming_full_name}" to person "${row.matched_full_name}"?\n\n` +
                    `Marks them as the same human. Nothing is merged or deleted; the contact keeps its emails.`
                  : `Merge "${row.incoming_full_name}" into canonical?\n\n` +
                    `This re-keys all announcement_contacts links to the canonical and DELETES the duplicate global_entity. Cannot be undone via the UI.`)}
              onReject={() => bulk.runIds('reject', [row.candidate_id],
                `Mark "${row.incoming_full_name}" as not a duplicate?\n\nFuture ingests won't re-queue this pair.`)}
              pending={bulk.progress?.running ?? false}
            />
          ))}
        </div>
      )}
      <QueuePager pg={pg} total={data?.total ?? 0} search={false} />
    </>
  );
}

function ContactRow({ row, selected, onToggleSelect, onApprove, onReject, pending }: {
  row: ContactMergeCandidateRow;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  const payload = row.incoming_payload as Record<string, unknown>;
  const dupEmployer = (payload.current_employer as string) || (payload.organization as string) || '';
  const dupTitle = (payload.job_title as string) || '';
  const badge = STATUS_BADGE[row.review_status];
  const isPending = row.review_status === 'pending';
  const isLink = row.match_method === 'cross_type';
  return (
    <div className={`rounded-lg border bg-surface p-4 transition-colors ${
      selected ? 'border-primary-300 bg-primary-50/40' : 'border-grey-200'
    }`}>
      <div className="flex items-start gap-4">
        {isPending && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            disabled={pending}
            aria-label="Select for bulk action"
            className="mt-1 h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
            <span className="text-[10px] text-grey-500 font-mono uppercase tracking-wide">{row.match_method}</span>
            <span className="text-[10px] text-grey-500">
              similarity {(row.similarity_score * 100).toFixed(0)}%
            </span>
            {row.matched_entity_type && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                row.matched_entity_type === 'person' ? 'bg-primary-50 text-primary-700' : 'bg-grey-100 text-grey-700'
              }`}>{row.matched_entity_type === 'person' ? 'Person (editorial)' : 'Contact (CRM)'}</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">
                {isLink ? 'Contact record (will link → person)' : 'Duplicate (will merge → canonical)'}
              </div>
              <div className="font-semibold text-grey-900">{row.incoming_full_name}</div>
              {dupEmployer && <div className="text-sm text-grey-600">{dupEmployer}</div>}
              {dupTitle && <div className="text-xs text-grey-500 mt-0.5">{dupTitle}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">{isLink ? 'Person' : 'Canonical (survives)'}</div>
              <div className="font-semibold text-grey-900">{row.matched_full_name || row.matched_contact_id.slice(0, 8) + '…'}</div>
              <div className="text-xs text-grey-400 font-mono mt-0.5">{row.matched_contact_id}</div>
            </div>
          </div>

          {row.reviewed_by && (
            <div className="mt-2 text-[11px] text-grey-500">
              {row.review_status === 'approved_merge' ? 'Merged by'
                : row.review_status === 'approved_link' ? 'Linked by'
                : row.review_status === 'superseded' ? 'Auto-resolved during batch run by'
                : row.review_status === 'expired' ? 'Expired (recorded by'
                : 'Rejected by'} {row.reviewed_by}
              {row.reviewed_at ? ` on ${new Date(row.reviewed_at).toLocaleString()}` : ''}
              {row.review_notes ? ` — ${row.review_notes}` : ''}
            </div>
          )}
        </div>

        {isPending && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={onApprove}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-success-50 text-success-700 border border-success-200 rounded hover:bg-success-100 disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {isLink ? 'Link' : 'Approve merge'}
            </button>
            <button
              onClick={onReject}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface text-grey-700 border border-grey-300 rounded hover:bg-grey-50 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" /> Not a duplicate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Companies list — sibling shape
// =============================================================================
function CompaniesList({ statusFilter, qc }: { statusFilter: StatusFilter; qc: ReturnType<typeof useQueryClient> }) {
  const pg = usePagedQueue(`companies:${statusFilter}`);
  const queryKey = ['merge-candidates', 'companies', statusFilter, pg.page, pg.q];
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: () => listCompanyMergeCandidates(statusFilter, pg.params),
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const bulk = useBulkMergeOps(
    approveCompanyMerge,
    rejectCompanyMerge,
    qc,
    `companies:${statusFilter}:${pg.page}:${pg.q}`,
  );

  if (isLoading) return <div className="text-sm text-grey-500">Loading candidates…</div>;
  if (error) return <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">Failed to load: {(error as Error).message}</div>;

  const rows = data?.items ?? [];
  const pendingIds = rows.filter(r => r.review_status === 'pending').map(r => r.candidate_id);

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <SelectAllCheckbox
            pendingIds={pendingIds}
            selected={bulk.selected}
            onSelectAll={bulk.selectAll}
            onClear={bulk.clearSelection}
          />
          <QueueCount total={data?.total ?? 0} offset={data?.offset ?? 0} shown={rows.length} />
        </div>
        <button onClick={() => refetch()} disabled={isRefetching} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-600 hover:text-grey-900 border border-grey-200 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <QueuePager pg={pg} total={data?.total ?? 0} search={true} />

      <BulkActionBar
        selectedCount={bulk.selected.size}
        approveLabel={`Approve ${bulk.selected.size} merge${bulk.selected.size === 1 ? '' : 's'}`}
        approveConfirm={
          `Approve ${bulk.selected.size} company merge${bulk.selected.size === 1 ? '' : 's'}?\n\n` +
          `For each pair this re-keys company_announcements + entity_relationships to the canonical, unions aliases, and DELETES the duplicate global_entity. Cannot be undone via the UI.`
        }
        rejectLabel={`Mark ${bulk.selected.size} as not duplicates`}
        rejectConfirm={
          `Mark ${bulk.selected.size} pair${bulk.selected.size === 1 ? '' : 's'} as not duplicates?\n\n` +
          `Future ingests won't re-queue these pairs unless the rejection is undone manually.`
        }
        progress={bulk.progress}
        onApprove={(ct) => bulk.runBulk('approve', ct)}
        onReject={(ct) => bulk.runBulk('reject', ct)}
        onCancel={bulk.cancelBulk}
        onClear={bulk.clearSelection}
        onDismissSummary={bulk.dismissSummary}
      />

      {rows.length === 0 ? (
        <EmptyState message="No company-merge candidates in this view." />
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <CompanyRow
              key={row.candidate_id}
              row={row}
              selected={bulk.selected.has(row.candidate_id)}
              onToggleSelect={() => bulk.toggleRow(row.candidate_id)}
              onApprove={() => bulk.runIds('approve', [row.candidate_id],
                `Merge "${row.incoming_company_name}" into canonical?\n\n` +
                `This re-keys company_announcements + entity_relationships to the canonical and DELETES the duplicate global_entity. Cannot be undone via the UI.`)}
              onReject={() => bulk.runIds('reject', [row.candidate_id],
                `Mark "${row.incoming_company_name}" as not a duplicate?\n\nFuture ingests won't re-queue this pair.`)}
              pending={bulk.progress?.running ?? false}
            />
          ))}
        </div>
      )}
      <QueuePager pg={pg} total={data?.total ?? 0} search={false} />
    </>
  );
}

function RevertMerge({ row }: { row: CompanyMergeCandidateRow }) {
  const qc = useQueryClient();
  const [manages, setManages] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setMsg(null);
    try {
      const dry = await revertCompanyMerge(row.candidate_id, { dry_run: true, duplicate_manages_survivor: manages });
      if (dry.error) { setMsg(`Cannot revert: ${dry.error}`); return; }
      const removed = Object.values(dry.removals || {}).flatMap((r) => r.aliases_removed);
      const plan = [
        `Revert the merge of "${row.incoming_company_name}" into "${row.matched_company_name ?? row.matched_company_id}"?`,
        '',
        `• Re-create "${dry.would_create?.name ?? row.incoming_company_name}"${dry.would_create?.ticker ? ` (ticker ${dry.would_create.ticker})` : dry.would_create?.domain ? ` (${dry.would_create.domain})` : ''}`,
        removed.length ? `• Take these names off the survivor: ${removed.join(', ')}` : '• No names to take off the survivor',
        dry.codes_to_move?.length ? `• Move the ticker code back (${dry.codes_to_move.length})` : '',
        manages ? '• Link it as the survivor\'s manager' : '',
        dry.assayer_rows?.length ? `• Re-point Assayer: ${dry.assayer_rows.map((r) => r.canonical_identifier || r.display_name).join(', ')}` : '',
        '• Mark the pair "not a duplicate" so it is never queued again',
        '',
        `Not reverted: ${dry.not_reverted ?? 'edges and links the merge moved'}.`,
      ].filter(Boolean).join('\n');
      if (!window.confirm(plan)) return;
      const done = await revertCompanyMerge(row.candidate_id, { dry_run: false, duplicate_manages_survivor: manages });
      if (done.error || !done.applied) { setMsg(`Revert failed: ${done.error ?? 'not applied'}`); return; }
      setMsg(`Reverted — re-created as ${done.entity_id?.slice(0, 8)}…${done.assayer_repointed ? `, ${done.assayer_repointed} Assayer row re-pointed` : ''}`);
      qc.invalidateQueries({ queryKey: ['merge-candidates'] });
    } catch (err: any) {
      setMsg(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      <button
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface text-danger-700 border border-danger-200 rounded hover:bg-danger-50 disabled:opacity-50"
      >
        <XCircle className="h-3.5 w-3.5" /> {busy ? 'Checking…' : 'Revert merge…'}
      </button>
      <label className="flex items-center gap-1 text-[10px] text-grey-500">
        <input type="checkbox" checked={manages} onChange={(e) => setManages(e.target.checked)} disabled={busy} />
        duplicate is the survivor&apos;s manager
      </label>
      {msg && <div className="text-[11px] text-grey-600 max-w-[16rem] text-right">{msg}</div>}
    </div>
  );
}

function CompanyRow({ row, selected, onToggleSelect, onApprove, onReject, pending }: {
  row: CompanyMergeCandidateRow;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  const payload = row.incoming_payload as Record<string, unknown>;
  const dupCode = (payload.canonical_identifier as string) || '';
  const dupDomain = (payload.canonical_domain as string) || '';
  const dupIndustry = (payload.industry as string) || '';
  const badge = STATUS_BADGE[row.review_status];
  const isPending = row.review_status === 'pending';
  return (
    <div className={`rounded-lg border bg-surface p-4 transition-colors ${
      selected ? 'border-primary-300 bg-primary-50/40' : 'border-grey-200'
    }`}>
      <div className="flex items-start gap-4">
        {isPending && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            disabled={pending}
            aria-label="Select for bulk action"
            className="mt-1 h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
            <span className="text-[10px] text-grey-500 font-mono uppercase tracking-wide">{row.match_method}</span>
            <span className="text-[10px] text-grey-500">
              similarity {(row.similarity_score * 100).toFixed(0)}%
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Duplicate (will merge → canonical)</div>
              <div className="font-semibold text-grey-900">{row.incoming_company_name}</div>
              {dupCode && <div className="text-sm text-grey-600 font-mono">{dupCode}</div>}
              {dupDomain && <div className="text-xs text-grey-500 mt-0.5">{dupDomain}</div>}
              {dupIndustry && <div className="text-xs text-grey-500 mt-0.5">{dupIndustry}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Canonical (survives)</div>
              <div className="font-semibold text-grey-900">{row.matched_company_name || row.matched_company_id.slice(0, 8) + '…'}</div>
              <div className="text-xs text-grey-400 font-mono mt-0.5">{row.matched_company_id}</div>
            </div>
          </div>

          {row.reviewed_by && (
            <div className="mt-2 text-[11px] text-grey-500">
              {row.review_status === 'approved_merge' ? 'Merged by'
                : row.review_status === 'superseded' ? 'Auto-resolved during batch run by'
                : row.review_status === 'expired' ? 'Expired (recorded by'
                : 'Rejected by'} {row.reviewed_by}
              {row.reviewed_at ? ` on ${new Date(row.reviewed_at).toLocaleString()}` : ''}
              {row.review_notes ? ` — ${row.review_notes}` : ''}
            </div>
          )}
        </div>

        {isPending && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={onApprove}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-success-50 text-success-700 border border-success-200 rounded hover:bg-success-100 disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Approve merge
            </button>
            <button
              onClick={onReject}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface text-grey-700 border border-grey-300 rounded hover:bg-grey-50 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" /> Not a duplicate
            </button>
          </div>
        )}
        {row.review_status === 'approved_merge' && <RevertMerge row={row} />}
      </div>
    </div>
  );
}

// =============================================================================
// Attribute Changes list — proposed canonical-attribute changes
// (display_name renames surfaced by drift sweep + historical replay)
// =============================================================================

const ATTRIBUTE_CHANGE_STATUS_BADGE: Record<AttributeChangeReviewStatus, { label: string; cls: string }> = {
  pending:         { label: 'Pending',     cls: 'bg-grey-100 text-grey-700 border border-grey-200' },
  approved_apply:  { label: 'Applied',     cls: 'bg-success-50 text-success-700 border border-success-200' },
  rejected_keep:   { label: 'Rejected',    cls: 'bg-danger-50 text-danger-700 border border-danger-200' },
  expired:         { label: 'Expired',     cls: 'bg-grey-50 text-grey-500 border border-grey-200' },
  superseded:      { label: 'Superseded',  cls: 'bg-info-50 text-info-700 border border-info-200' },
};

function AttributeChangesList({ statusFilter, qc }: { statusFilter: StatusFilter; qc: ReturnType<typeof useQueryClient> }) {
  const pg = usePagedQueue(`attribute-changes:${statusFilter}`);
  const queryKey = ['merge-candidates', 'attribute-changes', statusFilter, pg.page, pg.q];
  // Map the shared StatusFilter ('approved_merge' / 'rejected_separate') values
  // to the AttributeChange equivalents at fetch time. Other values match.
  const remapped: AttributeChangeReviewStatus | 'all' =
    statusFilter === 'approved_merge' || statusFilter === 'approved_link' ? 'approved_apply'
    : statusFilter === 'rejected_separate' ? 'rejected_keep'
    : statusFilter;
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: () => listAttributeChangeCandidates(remapped, pg.params),
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const bulk = useBulkMergeOps(
    approveAttributeChange,
    rejectAttributeChange,
    qc,
    `attribute-changes:${statusFilter}:${pg.page}:${pg.q}`,
  );

  if (isLoading) return <div className="text-sm text-grey-500">Loading candidates…</div>;
  if (error) return <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">Failed to load: {(error as Error).message}</div>;

  const rows = data?.items ?? [];
  const pendingIds = rows.filter(r => r.review_status === 'pending').map(r => r.candidate_id);

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <SelectAllCheckbox
            pendingIds={pendingIds}
            selected={bulk.selected}
            onSelectAll={bulk.selectAll}
            onClear={bulk.clearSelection}
          />
          <QueueCount total={data?.total ?? 0} offset={data?.offset ?? 0} shown={rows.length} />
        </div>
        <button onClick={() => refetch()} disabled={isRefetching} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-600 hover:text-grey-900 border border-grey-200 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <QueuePager pg={pg} total={data?.total ?? 0} search={false} />

      <BulkActionBar
        selectedCount={bulk.selected.size}
        approveLabel={`Apply ${bulk.selected.size} change${bulk.selected.size === 1 ? '' : 's'}`}
        approveConfirm={
          `Apply ${bulk.selected.size} attribute change${bulk.selected.size === 1 ? '' : 's'}?\n\n` +
          `For each pair this records the prior canonical_name as a 'rebrand' alias in entity_aliases, ` +
          `updates global_entities.canonical_name to the proposed value, and refreshes the name embedding ` +
          `inline. The change is auditable but immediately affects all reads from the canonical column.`
        }
        rejectLabel={`Keep current value on ${bulk.selected.size}`}
        rejectConfirm={
          `Mark ${bulk.selected.size} change${bulk.selected.size === 1 ? '' : 's'} as rejected (keep canonical)?\n\n` +
          `Future sweeps won't re-queue these proposals.`
        }
        progress={bulk.progress}
        onApprove={(ct) => bulk.runBulk('approve', ct)}
        onReject={(ct) => bulk.runBulk('reject', ct)}
        onCancel={bulk.cancelBulk}
        onClear={bulk.clearSelection}
        onDismissSummary={bulk.dismissSummary}
      />

      {rows.length === 0 ? (
        <EmptyState message="No attribute-change candidates in this view." />
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <AttributeChangeRow
              key={row.candidate_id}
              row={row}
              selected={bulk.selected.has(row.candidate_id)}
              onToggleSelect={() => bulk.toggleRow(row.candidate_id)}
              onApprove={() => bulk.runIds('approve', [row.candidate_id],
                `Apply "${row.proposed_value}" as canonical (was "${row.current_value || '?'}")?\n\n` +
                `Records the prior value as a rebrand alias and updates canonical_name. Affects all reads of this entity going forward.`)}
              onReject={() => bulk.runIds('reject', [row.candidate_id],
                `Keep "${row.current_value || row.proposed_value}" as canonical (reject "${row.proposed_value}")?\n\n` +
                `Future sweeps won't re-queue this proposal.`)}
              pending={bulk.progress?.running ?? false}
            />
          ))}
        </div>
      )}
      <QueuePager pg={pg} total={data?.total ?? 0} search={false} />
    </>
  );
}

function AttributeChangeRow({ row, selected, onToggleSelect, onApprove, onReject, pending }: {
  row: AttributeChangeCandidateRow;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  const badge = ATTRIBUTE_CHANGE_STATUS_BADGE[row.review_status];
  const isPending = row.review_status === 'pending';
  return (
    <div className={`rounded-lg border bg-surface p-4 transition-colors ${
      selected ? 'border-primary-300 bg-primary-50/40' : 'border-grey-200'
    }`}>
      <div className="flex items-start gap-4">
        {isPending && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            disabled={pending}
            aria-label="Select for bulk action"
            className="mt-1 h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
            <span className="text-[10px] uppercase tracking-wide font-mono text-grey-500">{row.attribute_type}</span>
            <span className="text-[10px] text-grey-500 font-mono uppercase tracking-wide">{row.source}</span>
            {row.match_method && (
              <span className="text-[10px] text-grey-500 font-mono">{row.match_method}</span>
            )}
            {row.similarity_score != null && (
              <span className="text-[10px] text-grey-500">
                confidence {(row.similarity_score * 100).toFixed(0)}%
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Current canonical (kept on reject)</div>
              <div className="font-semibold text-grey-900">{row.current_value ?? '—'}</div>
              <div className="text-xs text-grey-400 font-mono mt-0.5">{row.entity_id}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Proposed canonical (applied on approve)</div>
              <div className="font-semibold text-grey-900">{row.proposed_value}</div>
              {row.source_announcement_id && (
                <div className="text-xs text-grey-500 mt-0.5">via ann {row.source_announcement_id.slice(0, 8)}…</div>
              )}
            </div>
          </div>

          {row.reviewed_by && (
            <div className="mt-2 text-[11px] text-grey-500">
              {row.review_status === 'approved_apply' ? 'Applied by'
                : row.review_status === 'superseded' ? 'Superseded by'
                : row.review_status === 'expired' ? 'Expired (recorded by'
                : 'Rejected by'} {row.reviewed_by}
              {row.reviewed_at ? ` on ${new Date(row.reviewed_at).toLocaleString()}` : ''}
              {row.review_notes ? ` — ${row.review_notes}` : ''}
            </div>
          )}
        </div>

        {isPending && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={onApprove}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-success-50 text-success-700 border border-success-200 rounded hover:bg-success-100 disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Apply change
            </button>
            <button
              onClick={onReject}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface text-grey-700 border border-grey-300 rounded hover:bg-grey-50 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" /> Keep current
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Funds list — sibling shape (fund-shape entities: ETF / REIT / LIC /
// managed_fund / superannuation_fund / sovereign_fund / index)
// =============================================================================
function FundsList({ statusFilter, qc }: { statusFilter: StatusFilter; qc: ReturnType<typeof useQueryClient> }) {
  const pg = usePagedQueue(`funds:${statusFilter}`);
  const queryKey = ['merge-candidates', 'funds', statusFilter, pg.page, pg.q];
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: () => listFundMergeCandidates(statusFilter, pg.params),
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const bulk = useBulkMergeOps(
    approveFundMerge,
    rejectFundMerge,
    qc,
    `funds:${statusFilter}:${pg.page}:${pg.q}`,
  );

  if (isLoading) return <div className="text-sm text-grey-500">Loading candidates…</div>;
  if (error) return <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">Failed to load: {(error as Error).message}</div>;

  const rows = data?.items ?? [];
  const pendingIds = rows.filter(r => r.review_status === 'pending').map(r => r.candidate_id);

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <SelectAllCheckbox
            pendingIds={pendingIds}
            selected={bulk.selected}
            onSelectAll={bulk.selectAll}
            onClear={bulk.clearSelection}
          />
          <QueueCount total={data?.total ?? 0} offset={data?.offset ?? 0} shown={rows.length} />
        </div>
        <button onClick={() => refetch()} disabled={isRefetching} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-600 hover:text-grey-900 border border-grey-200 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <QueuePager pg={pg} total={data?.total ?? 0} search={true} />

      <BulkActionBar
        selectedCount={bulk.selected.size}
        approveLabel={`Approve ${bulk.selected.size} merge${bulk.selected.size === 1 ? '' : 's'}`}
        approveConfirm={
          `Approve ${bulk.selected.size} fund merge${bulk.selected.size === 1 ? '' : 's'}?\n\n` +
          `For each pair this unions aliases, re-keys any fund whose manager points to the duplicate, and DELETES the duplicate global_entity. Cannot be undone via the UI.`
        }
        rejectLabel={`Mark ${bulk.selected.size} as not duplicates`}
        rejectConfirm={
          `Mark ${bulk.selected.size} pair${bulk.selected.size === 1 ? '' : 's'} as not duplicates?\n\n` +
          `Future ingests won't re-queue these pairs unless the rejection is undone manually.`
        }
        progress={bulk.progress}
        onApprove={(ct) => bulk.runBulk('approve', ct)}
        onReject={(ct) => bulk.runBulk('reject', ct)}
        onCancel={bulk.cancelBulk}
        onClear={bulk.clearSelection}
        onDismissSummary={bulk.dismissSummary}
      />

      {rows.length === 0 ? (
        <EmptyState message="No fund-merge candidates in this view." />
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <FundRow
              key={row.candidate_id}
              row={row}
              selected={bulk.selected.has(row.candidate_id)}
              onToggleSelect={() => bulk.toggleRow(row.candidate_id)}
              onApprove={() => bulk.runIds('approve', [row.candidate_id],
                `Merge "${row.incoming_fund_name}" into canonical?\n\n` +
                `This unions aliases, re-keys any fund whose manager points to the duplicate, and DELETES the duplicate global_entity. Cannot be undone via the UI.`)}
              onReject={() => bulk.runIds('reject', [row.candidate_id],
                `Mark "${row.incoming_fund_name}" as not a duplicate?\n\nFuture ingests won't re-queue this pair.`)}
              pending={bulk.progress?.running ?? false}
            />
          ))}
        </div>
      )}
      <QueuePager pg={pg} total={data?.total ?? 0} search={false} />
    </>
  );
}

function FundRow({ row, selected, onToggleSelect, onApprove, onReject, pending }: {
  row: FundMergeCandidateRow;
  selected: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  const payload = row.incoming_payload as Record<string, unknown>;
  const dupTicker = (payload.ticker as string) || '';
  const dupArsn = (payload.arsn as string) || '';
  const dupApir = (payload.apir as string) || '';
  const dupIsin = (payload.isin as string) || '';
  const dupManager = (payload.manager_name as string) || '';
  const dupEntityType = (payload.entity_type as string) || '';
  const badge = STATUS_BADGE[row.review_status];
  const isPending = row.review_status === 'pending';
  return (
    <div className={`rounded-lg border bg-surface p-4 transition-colors ${
      selected ? 'border-primary-300 bg-primary-50/40' : 'border-grey-200'
    }`}>
      <div className="flex items-start gap-4">
        {isPending && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            disabled={pending}
            aria-label="Select for bulk action"
            className="mt-1 h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>
            {dupEntityType && (
              <span className="text-[10px] uppercase tracking-wide font-mono text-grey-500">{dupEntityType}</span>
            )}
            <span className="text-[10px] text-grey-500 font-mono uppercase tracking-wide">{row.match_method}</span>
            <span className="text-[10px] text-grey-500">
              similarity {(row.similarity_score * 100).toFixed(0)}%
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Duplicate (will merge → canonical)</div>
              <div className="font-semibold text-grey-900">{row.incoming_fund_name}</div>
              {dupTicker && <div className="text-sm text-grey-600 font-mono">{dupTicker}</div>}
              {dupArsn && <div className="text-xs text-grey-500 mt-0.5">ARSN {dupArsn}</div>}
              {dupApir && <div className="text-xs text-grey-500 mt-0.5">APIR {dupApir}</div>}
              {dupIsin && <div className="text-xs text-grey-500 mt-0.5">ISIN {dupIsin}</div>}
              {dupManager && <div className="text-xs text-grey-500 mt-0.5">Manager: {dupManager}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Canonical (survives)</div>
              <div className="font-semibold text-grey-900">{row.matched_fund_name || row.matched_fund_id.slice(0, 8) + '…'}</div>
              <div className="text-xs text-grey-400 font-mono mt-0.5">{row.matched_fund_id}</div>
            </div>
          </div>

          {row.reviewed_by && (
            <div className="mt-2 text-[11px] text-grey-500">
              {row.review_status === 'approved_merge' ? 'Merged by'
                : row.review_status === 'superseded' ? 'Auto-resolved during batch run by'
                : row.review_status === 'expired' ? 'Expired (recorded by'
                : 'Rejected by'} {row.reviewed_by}
              {row.reviewed_at ? ` on ${new Date(row.reviewed_at).toLocaleString()}` : ''}
              {row.review_notes ? ` — ${row.review_notes}` : ''}
            </div>
          )}
        </div>

        {isPending && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={onApprove}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-success-50 text-success-700 border border-success-200 rounded hover:bg-success-100 disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Approve merge
            </button>
            <button
              onClick={onReject}
              disabled={pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface text-grey-700 border border-grey-300 rounded hover:bg-grey-50 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" /> Not a duplicate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Shared bulk-ops machinery
// =============================================================================
type BulkProgress = {
  running: boolean;
  total: number;
  done: number;
  failures: Array<{ id: string; error: string }>;
};

/**
 * Hook owning selection state + sequential bulk-processor for the merge
 * queues. Contacts + companies wire it independently with their own
 * per-row endpoints. Sequential (not parallel) so the operator hits the
 * same NOT-EXISTS PK-collision guards the per-row path already exercises;
 * 119 rows × ~200ms each ≈ 25s for the worst case, which is acceptable
 * for a maintenance batch.
 *
 * Selection auto-clears on status-filter / tab change because the visible
 * row set changes; processed rows fall out of the pending view naturally.
 */
function useBulkMergeOps(
  approveFn: (id: string) => Promise<unknown>,
  rejectFn: (id: string) => Promise<unknown>,
  qc: ReturnType<typeof useQueryClient>,
  resetKey: string,
) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<BulkProgress | null>(null);
  const cancelledRef = useRef(false);

  // Reset selection whenever the caller signals a context change
  // (status filter flip, tab flip).
  useEffect(() => {
    setSelected(new Set());
    setProgress(null);
    cancelledRef.current = false;
  }, [resetKey]);

  const toggleRow = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = (ids: string[]) => setSelected(new Set(ids));
  const clearSelection = () => setSelected(new Set());

  // Explicit ids so per-row callers don't have to round-trip through
  // selection state. Bulk callers pass Array.from(selected); per-row
  // callers pass [thisId].
  const runIds = async (kind: 'approve' | 'reject', ids: string[], confirmText: string) => {
    if (ids.length === 0) return;
    if (!window.confirm(confirmText)) return;

    cancelledRef.current = false;
    const fn = kind === 'approve' ? approveFn : rejectFn;
    const failures: Array<{ id: string; error: string }> = [];
    setProgress({ running: true, total: ids.length, done: 0, failures: [] });

    for (let i = 0; i < ids.length; i++) {
      if (cancelledRef.current) break;
      const id = ids[i];
      try {
        await fn(id);
      } catch (e: any) {
        failures.push({ id, error: e?.response?.data?.detail || e?.message || String(e) });
      }
      setProgress({
        running: !cancelledRef.current && i < ids.length - 1,
        total: ids.length,
        done: i + 1,
        failures: [...failures],
      });
    }

    setSelected(new Set());
    qc.invalidateQueries({ queryKey: ['merge-candidates'] });
  };

  const runBulk = (kind: 'approve' | 'reject', confirmText: string) =>
    runIds(kind, Array.from(selected), confirmText);

  const cancelBulk = () => { cancelledRef.current = true; };
  const dismissSummary = () => setProgress(null);

  return {
    selected,
    toggleRow,
    selectAll,
    clearSelection,
    progress,
    runBulk,
    runIds,
    cancelBulk,
    dismissSummary,
  };
}

/**
 * Sticky bar that appears above the row list whenever the operator has
 * one or more rows checked. Surfaces the count, the two bulk actions,
 * and progress / summary state.
 */
function BulkActionBar({
  selectedCount,
  approveLabel,
  approveConfirm,
  rejectLabel,
  rejectConfirm,
  progress,
  onApprove,
  onReject,
  onCancel,
  onClear,
  onDismissSummary,
}: {
  selectedCount: number;
  approveLabel: string;
  approveConfirm: string;
  rejectLabel: string;
  rejectConfirm: string;
  progress: BulkProgress | null;
  onApprove: (confirmText: string) => void;
  onReject: (confirmText: string) => void;
  onCancel: () => void;
  onClear: () => void;
  onDismissSummary: () => void;
}) {
  // Mid-run progress strip
  if (progress?.running) {
    return (
      <div className="sticky top-0 z-10 mb-3 rounded-lg border border-info-200 bg-info-50 p-3 flex items-center gap-3">
        <Loader2 className="h-4 w-4 text-info-700 animate-spin flex-shrink-0" />
        <div className="flex-1 text-sm text-info-700">
          Processing {progress.done} of {progress.total}
          {progress.failures.length > 0 && (
            <span className="ml-2 text-danger-700">· {progress.failures.length} failed so far</span>
          )}
        </div>
        <button
          onClick={onCancel}
          className="text-xs font-semibold text-info-700 hover:text-info-900 underline-offset-2 hover:underline"
        >
          Stop after current
        </button>
      </div>
    );
  }

  // Post-run summary (shown until dismissed or selection changes)
  if (progress && !progress.running) {
    const succeeded = progress.done - progress.failures.length;
    return (
      <div className={`sticky top-0 z-10 mb-3 rounded-lg border p-3 flex items-start gap-3 ${
        progress.failures.length === 0
          ? 'border-success-200 bg-success-50'
          : 'border-warning-200 bg-warning-50'
      }`}>
        {progress.failures.length === 0
          ? <CheckCircle2 className="h-4 w-4 text-success-700 flex-shrink-0 mt-0.5" />
          : <AlertCircle className="h-4 w-4 text-warning-700 flex-shrink-0 mt-0.5" />}
        <div className="flex-1 text-sm">
          <div className={progress.failures.length === 0 ? 'text-success-700' : 'text-warning-700'}>
            <span className="font-semibold">{succeeded}</span> processed
            {progress.failures.length > 0 && (
              <span> · <span className="font-semibold">{progress.failures.length}</span> failed</span>
            )}
          </div>
          {progress.failures.length > 0 && (
            <ul className="mt-1.5 text-xs text-grey-700 space-y-0.5 max-h-28 overflow-auto">
              {progress.failures.slice(0, 10).map(f => (
                <li key={f.id} className="font-mono">
                  <span className="text-grey-500">{f.id.slice(0, 8)}…</span> {f.error}
                </li>
              ))}
              {progress.failures.length > 10 && (
                <li className="text-grey-500 italic">+ {progress.failures.length - 10} more</li>
              )}
            </ul>
          )}
        </div>
        <button
          onClick={onDismissSummary}
          className="text-xs font-semibold text-grey-600 hover:text-grey-900"
        >
          Dismiss
        </button>
      </div>
    );
  }

  // Selection bar (idle, awaiting bulk action)
  if (selectedCount === 0) return null;

  return (
    <div className="sticky top-0 z-10 mb-3 rounded-lg border border-primary-200 bg-primary-50 p-3 flex items-center gap-3">
      <div className="flex-1 text-sm font-medium text-primary-800">
        <span className="font-bold tabular-nums">{selectedCount}</span> selected
      </div>
      <button
        onClick={() => onApprove(approveConfirm)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-success-600 text-white rounded hover:bg-success-700"
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> {approveLabel}
      </button>
      <button
        onClick={() => onReject(rejectConfirm)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-surface text-grey-700 border border-grey-300 rounded hover:bg-grey-50"
      >
        <XCircle className="h-3.5 w-3.5" /> {rejectLabel}
      </button>
      <button
        onClick={onClear}
        className="text-xs font-semibold text-grey-600 hover:text-grey-900"
      >
        Clear
      </button>
    </div>
  );
}

/** Tri-state header checkbox: unchecked, indeterminate (some), checked (all). */
function SelectAllCheckbox({
  pendingIds,
  selected,
  onSelectAll,
  onClear,
}: {
  pendingIds: string[];
  selected: Set<string>;
  onSelectAll: (ids: string[]) => void;
  onClear: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const selectedPending = pendingIds.filter(id => selected.has(id)).length;
  const allChecked = pendingIds.length > 0 && selectedPending === pendingIds.length;
  const someChecked = selectedPending > 0 && selectedPending < pendingIds.length;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someChecked;
  }, [someChecked]);

  if (pendingIds.length === 0) return null;

  return (
    <label className="flex items-center gap-2 text-sm text-grey-700 cursor-pointer select-none">
      <input
        ref={ref}
        type="checkbox"
        checked={allChecked}
        onChange={e => (e.target.checked ? onSelectAll(pendingIds) : onClear())}
        className="h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500"
      />
      <span>
        {selectedPending > 0
          ? `${selectedPending} of ${pendingIds.length} pending selected`
          : `Select all ${pendingIds.length} pending on this page`}
      </span>
    </label>
  );
}



// =============================================================================
// Identities (2026-09-22) — person rows that may be one human, one decision per
// group. Groups come from same names across boards, contacts that tie two
// persons together (witnesses) and pending candidates; nothing merges unless
// the operator merges it here.
// =============================================================================
const ROLE_LABEL: Record<string, string> = {
  director_executive: 'Executive director', director_non_executive: 'Non-executive director',
  director_independent: 'Independent director', chair: 'Chair', deputy_chair: 'Deputy chair',
  managing_director: 'Managing director', ceo: 'CEO', cfo: 'CFO', coo: 'COO', cto: 'CTO',
  company_secretary: 'Company secretary', auditor: 'Auditor', other: 'Other role',
};

function IdentitiesList({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const pg = usePagedQueue('identities', 10);
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['merge-candidates', 'identities', pg.page, pg.q],
    queryFn: () => listIdentityGroups(pg.params),
    placeholderData: keepPreviousData,
  });
  if (isLoading) return <div className="text-sm text-grey-500">Loading identity groups…</div>;
  if (error) return <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">Failed to load: {(error as Error).message}</div>;
  const groups = data?.items ?? [];
  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-sm text-grey-600">
          <strong>{(data?.total ?? 0).toLocaleString()}</strong> identit{data?.total === 1 ? 'y' : 'ies'} to review:
          person records that may be one human. Groups with a contact tying two of them together come first.
        </div>
        <button onClick={() => refetch()} disabled={isRefetching} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-600 hover:text-grey-900 border border-grey-200 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <QueuePager pg={pg} total={data?.total ?? 0} search={true} />
      {groups.length === 0 ? (
        <EmptyState message="No identity groups in this view." />
      ) : (
        <div className="space-y-4">
          {groups.map(g => <IdentityCard key={g.group_key} group={g} onDone={() => qc.invalidateQueries({ queryKey: ['merge-candidates'] })} />)}
        </div>
      )}
      <QueuePager pg={pg} total={data?.total ?? 0} search={false} />
    </>
  );
}

function IdentityCard({ group, onDone }: { group: IdentityGroup; onDone: () => void }) {
  const [survivor, setSurvivor] = useState(group.persons[0]?.entity_id ?? '');
  const [included, setIncluded] = useState<Set<string>>(new Set(group.persons.map(p => p.entity_id)));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const nameOf = (id: string | null) => group.persons.find(p => p.entity_id === id)?.name ?? group.contacts.find(c => c.entity_id === id)?.name ?? '';
  const losers = group.persons.filter(p => included.has(p.entity_id) && p.entity_id !== survivor);
  const ev = group.evidence;

  const run = async (fn: () => Promise<string>) => {
    setBusy(true); setMessage(null);
    try { setMessage(await fn()); onDone(); }
    catch (err: any) { setMessage(err?.response?.data?.detail || err?.message || 'Request failed'); }
    finally { setBusy(false); }
  };
  const merge = () => {
    if (!losers.length) return;
    const survivorName = nameOf(survivor);
    if (!window.confirm(
      `Merge ${losers.length} person record${losers.length === 1 ? '' : 's'} into "${survivorName}"?\n\n` +
      losers.map(p => `• ${p.name} (${p.role_count} role${p.role_count === 1 ? '' : 's'})`).join('\n') +
      `\n\nTheir boards, tenures, filings and contact links move to "${survivorName}". ` +
      `The merged records are archived and deleted; the archive allows a reversal, the UI does not.`
    )) return;
    run(async () => {
      const r = await mergeIdentityGroup(survivor, losers.map(p => p.entity_id));
      return `Merged ${losers.length} into ${survivorName}; ${r.contacts_linked} contact${r.contacts_linked === 1 ? '' : 's'} linked, ${r.candidates_closed} candidate${r.candidates_closed === 1 ? '' : 's'} closed.`;
    });
  };
  const separate = (id: string) => {
    const others = group.persons.map(p => p.entity_id).filter(x => x !== id);
    if (!window.confirm(`Record "${nameOf(id)}" as a different human from the other ${others.length} record${others.length === 1 ? '' : 's'} in this group?\n\nThey will not be grouped again.`)) return;
    run(async () => {
      const r = await separateIdentity(id, others);
      return `Recorded as different; ${r.candidates_rejected} pending candidate${r.candidates_rejected === 1 ? '' : 's'} rejected.`;
    });
  };
  const unlink = (contactId: string, personId: string) => {
    if (!window.confirm(`Unlink contact "${nameOf(contactId)}" from "${nameOf(personId)}"?\n\nThe link is ended (kept on record) and the pair is remembered as different people, so no later filing re-links them.`)) return;
    run(async () => { await unlinkIdentityContact(contactId, personId); return 'Unlinked.'; });
  };
  const decide = (candidateId: string, approve: boolean) =>
    run(async () => {
      if (approve) { await approveContactMerge(candidateId); return 'Linked.'; }
      await rejectContactMerge(candidateId); return 'Marked as not the same.';
    });

  return (
    <div className="rounded-lg border border-grey-200 bg-surface p-4">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="font-semibold text-grey-900">{group.persons[0]?.name}</span>
        <span className="text-xs text-grey-500">{group.persons.length} person records{group.contacts.length ? ` · ${group.contacts.length} contact records` : ''}</span>
        {ev.witness_contacts.length > 0 && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success-50 text-success-700 border border-success-200">
            {ev.witness_contacts.length} contact{ev.witness_contacts.length === 1 ? '' : 's'} tie these records together
          </span>
        )}
        {ev.shared_email && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success-50 text-success-700 border border-success-200">Shared email address</span>}
        {ev.company_secretary_boards >= 2 && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-info-50 text-info-700 border border-info-200">Company secretary on {ev.company_secretary_boards} boards</span>
        )}
        {ev.same_name_only && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-grey-100 text-grey-700 border border-grey-200">Same name only</span>}
      </div>

      <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Person records: keep one, merge the ones that are the same human</div>
      <div className="space-y-2 mb-3">
        {group.persons.map(p => (
          <div key={p.entity_id} className={`rounded border p-2 ${p.entity_id === survivor ? 'border-primary-300 bg-primary-50/40' : 'border-grey-200'}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-1 text-xs text-grey-600">
                <input type="radio" name={`survivor-${group.group_key}`} checked={p.entity_id === survivor}
                  onChange={() => { setSurvivor(p.entity_id); setIncluded(prev => new Set(prev).add(p.entity_id)); }} disabled={busy} />
                Keep
              </label>
              <label className="flex items-center gap-1 text-xs text-grey-600">
                <input type="checkbox" checked={included.has(p.entity_id)} disabled={busy || p.entity_id === survivor}
                  onChange={() => setIncluded(prev => { const n = new Set(prev); if (n.has(p.entity_id)) n.delete(p.entity_id); else n.add(p.entity_id); return n; })} />
                Same human
              </label>
              <span className="font-medium text-grey-900">{p.name}</span>
              <span className="text-[11px] text-grey-500">first seen {p.first_seen ? new Date(p.first_seen).toLocaleDateString() : '—'}</span>
              <button onClick={() => separate(p.entity_id)} disabled={busy}
                className="ml-auto text-[11px] px-2 py-0.5 border border-grey-300 rounded text-grey-700 hover:bg-grey-50 disabled:opacity-50">
                Not the same as the others
              </button>
            </div>
            {p.roles.length > 0 ? (
              <ul className="mt-1 text-xs text-grey-600 space-y-0.5">
                {p.roles.map((r, i) => (
                  <li key={i}>
                    {r.company} · {ROLE_LABEL[r.role] ?? r.role} · {r.valid_from}{r.valid_to ? ` → ${r.valid_to}` : ' → current'}
                  </li>
                ))}
                {p.role_count > p.roles.length && <li className="text-grey-400">+{p.role_count - p.roles.length} more</li>}
              </ul>
            ) : <div className="mt-1 text-xs text-grey-400">No recorded roles</div>}
          </div>
        ))}
      </div>

      {group.contacts.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Contact records (announcement contact lines)</div>
          <ul className="text-xs text-grey-700 space-y-0.5 mb-3">
            {group.contacts.map(c => (
              <li key={c.entity_id}>
                <span className="font-medium">{c.name}</span>
                {c.organisation ? ` · ${c.organisation}` : ''}{c.title ? ` · ${c.title}` : ''}
                {c.email_count ? ` · ${c.email_count} email${c.email_count === 1 ? '' : 's'} (${c.email_domains.join(', ')})` : ' · no email'}
                {c.linked_person_id ? ` · linked to ${nameOf(c.linked_person_id)}` : ' · not linked'}
                {c.linked_person_id && (
                  <button onClick={() => unlink(c.entity_id, c.linked_person_id!)} disabled={busy}
                    className="ml-2 px-1.5 py-0.5 text-[11px] border border-grey-300 rounded text-grey-700 hover:bg-grey-50 disabled:opacity-50">
                    Unlink
                  </button>
                )}
                {group.evidence.witness_contacts.includes(c.entity_id) && <span className="ml-1 text-success-700">(ties records together)</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {group.candidates.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wide text-grey-500 mb-1">Pending decisions in this group</div>
          <ul className="text-xs text-grey-700 space-y-1 mb-3">
            {group.candidates.map(c => (
              <li key={c.candidate_id} className="flex items-center gap-2 flex-wrap">
                <span>{c.effect}</span>
                <button onClick={() => decide(c.candidate_id, true)} disabled={busy}
                  className="px-2 py-0.5 border border-success-200 bg-success-50 text-success-700 rounded disabled:opacity-50">
                  {c.match_method === 'cross_type' ? 'Link' : 'Approve'}
                </button>
                <button onClick={() => decide(c.candidate_id, false)} disabled={busy}
                  className="px-2 py-0.5 border border-grey-300 rounded text-grey-700 disabled:opacity-50">Not the same</button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={merge} disabled={busy || losers.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />}
          Merge {losers.length} into {nameOf(survivor) || 'the kept record'}
        </button>
        {message && <span className="text-xs text-grey-700">{message}</span>}
      </div>
    </div>
  );
}

// =============================================================================
// Paging (2026-09-22) — the tabs fetched a fixed 500 and reported that as the
// count. One page at a time with the queue's real total, like the LinkedIn tags
// review; the name filter runs server-side over both sides of the pair.
// =============================================================================
const PAGE_SIZE = 50;

function usePagedQueue(resetKey: string, pageSize: number = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { setPage(1); }, [resetKey, q]);
  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);
  return {
    page, setPage, input, setInput, q,
    pageSize,
    params: { limit: pageSize, offset: (page - 1) * pageSize, q },
  };
}

type PagedQueue = ReturnType<typeof usePagedQueue>;

function QueueCount({ total, offset, shown }: { total: number; offset: number; shown: number }) {
  return (
    <div className="text-sm text-grey-500">
      {total.toLocaleString()} candidate{total === 1 ? '' : 's'}
      {total > shown && shown > 0 ? ` (showing ${offset + 1}–${offset + shown})` : ''}
    </div>
  );
}

function QueuePager({ pg, total, search }: { pg: PagedQueue; total: number; search: boolean }) {
  const pageCount = Math.max(1, Math.ceil(total / pg.pageSize));
  // Rows leave a pending queue as they are reviewed; snap back rather than
  // show an empty page under a non-zero total.
  useEffect(() => {
    if (pg.page > pageCount) pg.setPage(pageCount);
  }, [pg, pageCount]);
  if (!search && pageCount <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
      {search ? (
        <input
          value={pg.input}
          onChange={e => pg.setInput(e.target.value)}
          placeholder="Filter by name (either side)"
          className="px-3 py-1.5 text-sm border border-grey-300 rounded w-64 max-w-full"
        />
      ) : <span />}
      {pageCount > 1 && (
        <div className="flex items-center gap-2 text-sm text-grey-600">
          <span>Page {pg.page} of {pageCount}</span>
          <button disabled={pg.page <= 1} onClick={() => pg.setPage(pg.page - 1)}
            className="px-2 py-1 text-xs border border-grey-200 rounded disabled:opacity-40">Prev</button>
          <button disabled={pg.page >= pageCount} onClick={() => pg.setPage(pg.page + 1)}
            className="px-2 py-1 text-xs border border-grey-200 rounded disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Shared bits
// =============================================================================
function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-grey-200 bg-grey-50 p-8 text-center">
      <AlertCircle className="h-8 w-8 text-grey-300 mx-auto mb-2" />
      <p className="text-sm text-grey-600">{message}</p>
    </div>
  );
}
